import { A } from "@solidjs/router";
import { type Component, createEffect, For } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePostHog } from "@/hooks/usePostHog";

const UPGRADE_HREF = "/profile/company/upgrade";

export type UpsellModalSource = "card" | "sheet";

export type UpsellModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Entry point the modal was opened from — attached to tracking events. */
  source: UpsellModalSource;
};

/**
 * Dumb, controlled upsell modal shown when a free-plan company tries to save a
 * freelancer to the Talent Pool. It holds no capabilities logic — the caller
 * owns `open`/`onOpenChange`. Visual language follows the "Talent Pool — Piano
 * Free" handoff: a tinted, full-bleed header with a brand lock badge, then a
 * value line + benefit checklist, closed by two vertically-stacked CTAs
 * (primary above secondary). Copy is about *saving*, never about a retrieval
 * surface (D14). `DialogContent` renders its own top-right close button.
 */
export const UpsellModal: Component<UpsellModalProps> = (props) => {
  const { t } = useI18n();
  const { capture } = usePostHog();

  // Derived (reactive to language) — three named benefit keys composed into a
  // list rendered with <For>.
  const benefits = () => [
    t("company.talent_pool.upsell.benefits.unlimited"),
    t("company.talent_pool.upsell.benefits.notes"),
    t("company.talent_pool.upsell.benefits.sharing"),
  ];

  // Fire the view event exactly once per closed→open transition (D10). A plain
  // instance flag tracks the previous open state so re-opening fires again but
  // a mount-while-closed (or source change while open) fires nothing.
  let wasOpen = false;
  createEffect(() => {
    const isOpen = props.open;
    if (isOpen && !wasOpen) {
      capture("view_talent_pool_upsell", { source: props.source });
    }
    wasOpen = isOpen;
  });

  const handlePrimaryClick = () => {
    // Navigation still proceeds via the <A> link; we only record the intent.
    capture("click_talent_pool_upsell_cta", { source: props.source });
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent class="gap-0 overflow-hidden p-0 sm:max-w-md">
        {/* Tinted, full-bleed header — brand lock badge + title, left-aligned.
            The built-in DialogContent close button sits in the top-right. */}
        <div class="flex flex-col gap-3 border-b border-border bg-linear-to-br from-primary/5 to-card p-6">
          <div class="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <i class="ph-fill ph-lock-simple text-2xl" aria-hidden="true" />
          </div>
          <DialogTitle class="text-xl">
            {t("company.talent_pool.upsell.title")}
          </DialogTitle>
        </div>

        {/* Body — value line, benefit checklist, stacked CTAs. */}
        <div class="flex flex-col gap-5 p-6">
          <DialogDescription class="text-sm leading-relaxed text-muted-foreground">
            {t("company.talent_pool.upsell.value")}
          </DialogDescription>

          <ul class="flex flex-col gap-2.5 text-sm text-gray-700">
            <For each={benefits()}>
              {(benefit) => (
                <li class="flex items-center gap-2.5">
                  <i
                    class="ph-fill ph-check-circle shrink-0 text-base text-green-600"
                    aria-hidden="true"
                  />
                  <span>{benefit}</span>
                </li>
              )}
            </For>
          </ul>

          {/* Centered, vertically-stacked CTAs — primary above secondary. */}
          <div class="flex flex-col gap-2">
            <Button
              as={A}
              href={UPGRADE_HREF}
              onClick={handlePrimaryClick}
              size="lg"
              class="w-full"
            >
              {t("company.talent_pool.upsell.cta_primary")}
            </Button>
            <Button
              variant="ghost"
              onClick={() => props.onOpenChange(false)}
              class="w-full text-muted-foreground hover:text-foreground"
            >
              {t("company.talent_pool.upsell.cta_secondary")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
